import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { api } from 'common';
import MaterialTable from 'material-table';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import CarRentalOutlinedIcon from '@mui/icons-material/CarRentalOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import StarsOutlinedIcon from '@mui/icons-material/StarsOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import LanOutlinedIcon from '@mui/icons-material/LanOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import localization from '../components/Table/Localization';
import {
  C2,
  C2_FONT,
  c2TableTheme,
  c2TableOptions,
  c2TableStyle,
  c2Localization,
  c2PillTabsSx,
  C2TableCard,
  C2SectionHeading,
  C2EmptyState,
  C2Select,
  C2SummaryCard,
  C2ProgressBar,
  C2StatusChip,
} from '../components/Table/C2UserTable';
import { c2TextFieldSx } from '../components/conecta2ModalStyles';
import { useTranslation } from 'react-i18next';
import moment from 'moment/min/moment-with-locales';

const MONO = "'JetBrains Mono', 'Consolas', monospace";

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

function GroupLabel({ children }) {
  return (
    <Typography
      sx={{
        fontFamily: C2_FONT,
        fontWeight: 800,
        fontSize: '0.78rem',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: C2.muted,
        mb: 1,
        px: 0.5,
      }}
    >
      {children}
    </Typography>
  );
}

function LoadingBlock({ label }) {
  return (
    <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
      <CircularProgress size={32} sx={{ color: C2.violet }} />
      <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.88rem', color: C2.muted }}>{label}</Typography>
    </Box>
  );
}

function ScopePill({ icon: Icon, children }) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        py: 0.6,
        borderRadius: '999px',
        backgroundColor: C2.lavender,
        border: `1px solid ${C2.border}`,
        fontFamily: C2_FONT,
        fontSize: '0.8rem',
        color: C2.deep,
        maxWidth: '100%',
      }}
    >
      <Icon sx={{ fontSize: 16, color: C2.violet }} />
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {children}
      </Box>
    </Box>
  );
}

function GrowthChart({ series }) {
  const max = Math.max(1, ...series.map((s) => Math.max(s.drivers, s.customers)));
  const legend = [
    { label: 'Conductores', bg: `linear-gradient(180deg, ${C2.bright}, ${C2.violet})` },
    { label: 'Usuarios', bg: '#B8A9E0' },
  ];
  return (
    <Box sx={{ px: { xs: 0.5, sm: 1 }, pb: 1 }}>
      <Box sx={{ display: 'flex', gap: 2, mb: 1.5, px: 0.5 }}>
        {legend.map((l) => (
          <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '4px', background: l.bg }} />
            <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: C2.muted }}>{l.label}</Typography>
          </Box>
        ))}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: { xs: 0.75, sm: 1.5 },
          height: 180,
          px: 0.5,
          borderBottom: `1px solid ${C2.border}`,
          backgroundImage: `repeating-linear-gradient(to top, transparent 0, transparent 44px, ${C2.cellBorder} 44px, ${C2.cellBorder} 45px)`,
        }}
      >
        {series.map((s) => (
          <Box
            key={s.weekKey}
            sx={{ flex: 1, height: '100%', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '3px' }}
          >
            {[
              { v: s.drivers, bg: legend[0].bg, name: 'conductores' },
              { v: s.customers, bg: legend[1].bg, name: 'usuarios' },
            ].map((b) => (
              <Box
                key={b.name}
                title={`${b.v} ${b.name}`}
                sx={{
                  width: '42%',
                  maxWidth: 22,
                  height: `${Math.max(b.v ? 4 : 1, (b.v / max) * 100)}%`,
                  borderRadius: '6px 6px 2px 2px',
                  background: b.bg,
                  transition: 'height 0.3s',
                }}
              />
            ))}
          </Box>
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: { xs: 0.75, sm: 1.5 }, px: 0.5, mt: 0.75 }}>
        {series.map((s) => (
          <Typography
            key={s.weekKey}
            sx={{ flex: 1, textAlign: 'center', fontFamily: C2_FONT, fontSize: '0.72rem', color: C2.muted, whiteSpace: 'nowrap' }}
          >
            {moment(s.weekKey, 'YYYY-MM-DD').format('DD MMM')}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

function nameCell(name, sub) {
  return (
    <Box sx={{ textAlign: 'left' }}>
      <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.88rem', color: C2.sidebar }}>
        {name || '—'}
      </Typography>
      {sub ? (
        <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.75rem', color: C2.muted }}>{sub}</Typography>
      ) : null}
    </Box>
  );
}

function codeCell(code) {
  const v = code && String(code).trim();
  if (!v) return '—';
  return (
    <Box
      component="span"
      sx={{
        px: 1,
        py: 0.4,
        borderRadius: '8px',
        backgroundColor: '#FFFFFF',
        border: `1px dashed ${C2.border}`,
        fontFamily: MONO,
        fontSize: '0.8rem',
        fontWeight: 700,
        color: C2.deep,
        letterSpacing: '0.04em',
      }}
    >
      {v}
    </Box>
  );
}

function userCreatedMs(u) {
  const raw = u?.createdAt;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  if (!Number.isNaN(n) && n > 0) return n;
  const d = new Date(raw).getTime();
  return Number.isNaN(d) ? null : d;
}

function displayName(u) {
  if (!u) return '—';
  const n = `${u.firstName || ''} ${u.lastName || ''}`.trim();
  return n || u.email || u.id || '—';
}

function resolveLevel(d, normalizeNetworkState) {
  const net = normalizeNetworkState
    ? normalizeNetworkState(d, d.id)
    : { inNetwork: false };
  if (net.inNetwork) return { level: net.level, net };
  if (d.signupMode === 'city_special') {
    return {
      level: 0,
      net: {
        inNetwork: true,
        level: 0,
        used: Number(d.directSlotsUsed) || 0,
        total: Number(d.directSlotsTotal) || 50,
        principalUid: d.principalUid || d.id,
      },
    };
  }
  return { level: null, net };
}

/**
 * Admin: Métricas de red / crecimiento (optimizado).
 */
export default function Metricas() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const dispatch = useDispatch();
  const {
    fetchUsersOnce,
    fetchZonificacionCities,
    fetchLeaderWeeklyHistory,
    normalizeNetworkState,
    networkLevelLabel,
    formatDirectSlots,
    formatWeeklyProgress,
    getWeekKey,
    DIRECT_SLOTS_PER_LEVEL,
    WEEKLY_MIN_DRIVERS,
    WEEKLY_MIN_CUSTOMERS,
  } = api;

  const staticusers = useSelector((s) => s.usersdata?.staticusers);
  const citiesRaw = useSelector((s) => s.zonificacion?.cities);
  const historyRaw = useSelector((s) => s.leaderAlerts?.history);
  const historyLoading = useSelector((s) => s.leaderAlerts?.historyLoading);
  const loadingUsers = useSelector((s) => s.usersdata?.loading);

  const [tab, setTab] = useState(0);
  const [cityFilter, setCityFilter] = useState('');
  const [driverFilter, setDriverFilter] = useState(null);
  const [historyRequested, setHistoryRequested] = useState(false);
  const [historyWeek, setHistoryWeek] = useState('');
  const [historyGoalFilter, setHistoryGoalFilter] = useState('all');
  const [historyLeaderScope, setHistoryLeaderScope] = useState('leaders');

  const cities = useMemo(
    () => (citiesRaw || []).filter((c) => c.active !== false),
    [citiesRaw]
  );

  const history = useMemo(() => historyRaw || [], [historyRaw]);

  useEffect(() => {
    // Sin locations: acelera mucho el primer paint de Métricas
    dispatch(fetchUsersOnce({ includeLocations: false }));
    dispatch(fetchZonificacionCities());
  }, [dispatch, fetchUsersOnce, fetchZonificacionCities]);

  // Historial solo cuando se abre el tab (evita bloquear el arranque)
  useEffect(() => {
    if ((tab === 1 || historyRequested) && fetchLeaderWeeklyHistory) {
      dispatch(fetchLeaderWeeklyHistory());
      setHistoryRequested(true);
    }
  }, [tab, historyRequested, dispatch, fetchLeaderWeeklyHistory]);

  const currentWeek = useMemo(
    () => (getWeekKey ? getWeekKey() : null),
    [getWeekKey]
  );

  useEffect(() => {
    if (!historyWeek && currentWeek) {
      setHistoryWeek(currentWeek);
    }
  }, [currentWeek, historyWeek]);

  const usersById = useMemo(() => {
    const m = {};
    const list = staticusers || [];
    for (let i = 0; i < list.length; i += 1) {
      const u = list[i];
      m[u.id] = u;
    }
    return m;
  }, [staticusers]);

  /** Índice hijos por patrocinador (una sola pasada). */
  const childrenBySponsor = useMemo(() => {
    const map = Object.create(null);
    const list = staticusers || [];
    for (let i = 0; i < list.length; i += 1) {
      const u = list[i];
      const s = u.sponsorUid || u.signupViaReferral;
      if (!s || !String(s).trim()) continue;
      const key = String(s).trim();
      if (!map[key]) map[key] = [];
      map[key].push(u.id);
    }
    return map;
  }, [staticusers]);

  /** Set de descendientes del conductor filtrado (BFS). */
  const scopeIdSet = useMemo(() => {
    if (!driverFilter?.id) return null;
    const rootId = driverFilter.id;
    const set = new Set([rootId]);
    const queue = [rootId];
    while (queue.length) {
      const id = queue.pop();
      const kids = childrenBySponsor[id] || [];
      for (let i = 0; i < kids.length; i += 1) {
        const k = kids[i];
        if (!set.has(k)) {
          set.add(k);
          queue.push(k);
        }
      }
    }
    const list = staticusers || [];
    for (let i = 0; i < list.length; i += 1) {
      const u = list[i];
      if (u.principalUid === rootId) set.add(u.id);
    }
    return set;
  }, [driverFilter, childrenBySponsor, staticusers]);

  const metrics = useMemo(() => {
    const list = staticusers || [];
    const slotDefault = DIRECT_SLOTS_PER_LEVEL || 50;
    const weekKeys = [];
    if (getWeekKey) {
      const now = Date.now();
      for (let i = 7; i >= 0; i -= 1) {
        weekKeys.push(getWeekKey(new Date(now - i * 7 * 86400000)));
      }
    }
    const uniqueWeeks = [...new Set(weekKeys)];
    const seriesMap = {};
    uniqueWeeks.forEach((wk) => {
      seriesMap[wk] = { weekKey: wk, drivers: 0, customers: 0, l2: 0, l3: 0 };
    });

    let allDrivers = 0;
    let allCustomers = 0;
    let scopedDrivers = 0;
    let scopedCustomers = 0;
    let newDrivers = 0;
    let newCustomers = 0;
    const levelCounts = { 0: 0, 1: 0, 2: 0, 3: 0 };
    let l2Week = 0;
    let l3Week = 0;
    let l1Used = 0;
    let l1Total = 0;
    let l1Principals = 0;
    let goalOn = 0;
    let goalOff = 0;
    let goalDone = 0;
    const networkRows = [];
    const driverOpts = [];

    const inScope = (u) => {
      if (cityFilter) {
        if (u.usertype === 'driver') {
          if (u.cityId !== cityFilter) return false;
        } else {
          if (u.cityId === cityFilter) {
            /* ok */
          } else {
            const sponsor = u.sponsorUid || u.signupViaReferral;
            if (!sponsor || usersById[sponsor]?.cityId !== cityFilter) return false;
          }
        }
      }
      if (scopeIdSet && !scopeIdSet.has(u.id)) return false;
      return true;
    };

    for (let i = 0; i < list.length; i += 1) {
      const u = list[i];
      const isDriver = u.usertype === 'driver';
      const isCustomer = u.usertype === 'customer';
      if (isDriver) allDrivers += 1;
      if (isCustomer) allCustomers += 1;
      if (!isDriver && !isCustomer) continue;
      if (!inScope(u)) continue;

      if (isDriver) scopedDrivers += 1;
      if (isCustomer) scopedCustomers += 1;

      const ms = userCreatedMs(u);
      const wk = ms != null && getWeekKey ? getWeekKey(new Date(ms)) : null;
      if (wk === currentWeek) {
        if (isDriver) newDrivers += 1;
        if (isCustomer) newCustomers += 1;
      }
      if (wk && seriesMap[wk]) {
        if (isDriver) seriesMap[wk].drivers += 1;
        if (isCustomer) seriesMap[wk].customers += 1;
      }

      if (!isDriver) continue;

      const { level, net } = resolveLevel(u, normalizeNetworkState);
      if (level == null) continue;

      const used = net.inNetwork ? Number(net.used) || 0 : Number(u.directSlotsUsed) || 0;
      const total = net.inNetwork
        ? net.total ?? slotDefault
        : level >= 3
          ? 0
          : Number(u.directSlotsTotal) || slotDefault;

      if (levelCounts[level] != null) levelCounts[level] += 1;
      if (wk === currentWeek) {
        if (level === 2) l2Week += 1;
        if (level === 3) l3Week += 1;
      }
      if (wk && seriesMap[wk]) {
        if (level === 2) seriesMap[wk].l2 += 1;
        if (level === 3) seriesMap[wk].l3 += 1;
      }

      if (level === 0) {
        l1Principals += 1;
        l1Used += used;
        l1Total += total || slotDefault;
        if (used >= (total || slotDefault)) {
          goalDone += 1;
        } else {
          const w = formatWeeklyProgress
            ? formatWeeklyProgress(u.leaderWeekly)
            : { driversOk: false, customersOk: false };
          if (w.driversOk && w.customersOk) goalOn += 1;
          else goalOff += 1;
        }
      }

      const row = {
        id: u.id,
        name: displayName(u),
        cityName: u.cityName || '',
        cityId: u.cityId || '',
        level,
        levelLabel: networkLevelLabel ? networkLevelLabel(level) : `Nivel ${level}`,
        used,
        total,
        remaining: Math.max(0, total - used),
        principalUid: net.principalUid || u.principalUid || (level === 0 ? u.id : ''),
        sponsorUid: u.sponsorUid || u.signupViaReferral || '',
        createdAt: u.createdAt,
        networkStructureStatus: u.networkStructureStatus,
        signupEntryCode: u.signupEntryCode || u.citySignupCode || '',
      };
      networkRows.push(row);

      // Autocomplete: solo red / líderes (más liviano)
      driverOpts.push({
        id: u.id,
        label: `${row.name} · ${row.levelLabel}${u.cityName ? ` · ${u.cityName}` : ''}`,
        cityId: u.cityId || '',
        level,
      });
    }

    driverOpts.sort((a, b) => String(a.label).localeCompare(String(b.label), 'es'));

    const leaders = l1Principals;
    const goalPct =
      leaders > 0 ? Math.round(((goalOn + goalDone) / leaders) * 100) : 0;

    return {
      allDrivers,
      allCustomers,
      scopedDrivers,
      scopedCustomers,
      newDrivers,
      newCustomers,
      levelCounts,
      l2Week,
      l3Week,
      l1Slots: {
        principals: l1Principals,
        used: l1Used,
        total: l1Total,
        available: Math.max(0, l1Total - l1Used),
      },
      weeklyGoal: {
        leaders,
        onTrack: goalOn,
        offTrack: goalOff,
        completedN1: goalDone,
        pct: goalPct,
      },
      networkRows,
      driverOptions: cityFilter
        ? driverOpts.filter((o) => o.cityId === cityFilter)
        : driverOpts,
      growthSeries: uniqueWeeks.map((wk) => seriesMap[wk]),
    };
  }, [
    staticusers,
    cityFilter,
    scopeIdSet,
    usersById,
    currentWeek,
    getWeekKey,
    normalizeNetworkState,
    networkLevelLabel,
    formatWeeklyProgress,
    DIRECT_SLOTS_PER_LEVEL,
  ]);

  const structureRows = useMemo(() => {
    const rows = metrics.networkRows;
    if (!driverFilter?.id) {
      return rows
        .filter((r) => r.level === 0)
        .map((r) => ({ ...r, role: 'Principal', parentName: '—' }));
    }
    const rootId = driverFilter.id;
    const root =
      rows.find((r) => r.id === rootId) || {
        id: rootId,
        name: displayName(usersById[rootId]),
        levelLabel: '—',
        cityName: usersById[rootId]?.cityName || '',
        used: 0,
        total: 50,
        createdAt: usersById[rootId]?.createdAt,
        networkStructureStatus: usersById[rootId]?.networkStructureStatus,
        signupEntryCode:
          usersById[rootId]?.signupEntryCode ||
          usersById[rootId]?.citySignupCode ||
          '',
      };
    const children = rows
      .filter((r) => r.id !== rootId)
      .map((r) => {
        const parent =
          usersById[r.sponsorUid] ||
          (r.principalUid ? usersById[r.principalUid] : null);
        return {
          ...r,
          role: r.levelLabel,
          parentName: displayName(parent),
        };
      })
      .sort((a, b) => (a.level || 0) - (b.level || 0));
    return [
      { ...root, role: 'Principal / seleccionado', parentName: '—' },
      ...children,
    ];
  }, [metrics.networkRows, driverFilter, usersById]);

  const START_WEEK_KEY = '2026-10-19';

  const availableWeeks = useMemo(() => {
    const set = new Set();
    if (currentWeek && currentWeek >= START_WEEK_KEY) set.add(currentWeek);
    if (getWeekKey) {
      const now = Date.now();
      for (let i = 0; i < 52; i += 1) {
        const wk = getWeekKey(new Date(now - i * 7 * 86400000));
        if (wk < START_WEEK_KEY) break;
        set.add(wk);
      }
    }
    (history || []).forEach((h) => {
      if (h.weekKey && h.weekKey >= START_WEEK_KEY) set.add(h.weekKey);
    });
    set.add(START_WEEK_KEY);
    const sorted = Array.from(set).sort((a, b) => String(b).localeCompare(String(a)));
    return sorted.map((wk) => {
      const isCur = wk === currentWeek;
      const labelDate = moment(wk, 'YYYY-MM-DD').format('DD MMM YYYY');
      return {
        value: wk,
        label: isCur ? `Semana ${labelDate} (Actual)` : `Semana ${labelDate}`,
      };
    });
  }, [currentWeek, getWeekKey, history]);

  const historyMap = useMemo(() => {
    const map = {};
    for (let i = 0; i < history.length; i += 1) {
      const h = history[i];
      const key = `${h.leaderUid}_${h.weekKey}`;
      map[key] = h;
    }
    return map;
  }, [history]);

  const historyRows = useMemo(() => {
    if (tab !== 1) return [];
    const dGoal = WEEKLY_MIN_DRIVERS || 2;
    const cGoal = WEEKLY_MIN_CUSTOMERS || 2;

    const list = staticusers || [];
    const leadersList = [];
    const leadersSet = new Set();
    for (let i = 0; i < list.length; i += 1) {
      const u = list[i];
      if (u.usertype !== 'driver') continue;
      const { level } = resolveLevel(u, normalizeNetworkState);
      if (level === 0 || u.signupMode === 'city_special') {
        leadersList.push(u);
        leadersSet.add(u.id);
      }
    }

    let rows = [];

    if (historyWeek && historyWeek !== 'all') {
      // Semana específica seleccionada
      if (historyLeaderScope === 'leaders') {
        // Métricas de los líderes principales (100 líderes)
        rows = leadersList.map((u) => {
          let drivers = 0;
          let customers = 0;
          let updatedAt = null;

          if (historyWeek === currentWeek) {
            const prog = formatWeeklyProgress ? formatWeeklyProgress(u.leaderWeekly) : null;
            drivers = prog?.drivers || 0;
            customers = prog?.customers || 0;
            updatedAt = u.leaderWeekly?.updatedAt || null;
            const histItem = historyMap[`${u.id}_${historyWeek}`];
            if (histItem) {
              if ((histItem.drivers || 0) > drivers) drivers = histItem.drivers;
              if ((histItem.customers || 0) > customers) customers = histItem.customers;
              if (!updatedAt) updatedAt = histItem.updatedAt;
            }
          } else {
            const histItem = historyMap[`${u.id}_${historyWeek}`];
            if (histItem) {
              drivers = histItem.drivers || 0;
              customers = histItem.customers || 0;
              updatedAt = histItem.updatedAt || null;
            }
          }

          const driversOk = drivers >= dGoal;
          const customersOk = customers >= cGoal;
          const metaOk = driversOk && customersOk;

          return {
            id: `${u.id}_${historyWeek}`,
            leaderUid: u.id,
            weekKey: historyWeek,
            leaderName: displayName(u),
            cityName: u.cityName || '',
            role: 'Líder 100',
            drivers,
            customers,
            driversLabel: `${drivers}/${dGoal}`,
            customersLabel: `${customers}/${cGoal}`,
            driversOk,
            customersOk,
            metaOk,
            updatedAt,
          };
        });
      } else {
        // Todos los conductores con registros en esa semana
        const weekHistory = history.filter((h) => h.weekKey === historyWeek);
        const mapUid = new Set(weekHistory.map((h) => h.leaderUid));
        rows = weekHistory.map((h) => {
          const u = usersById[h.leaderUid];
          const ok = (h.drivers || 0) >= dGoal && (h.customers || 0) >= cGoal;
          return {
            ...h,
            leaderName: displayName(u) !== '—' ? displayName(u) : h.leaderUid,
            cityName: u?.cityName || '',
            role: leadersSet.has(h.leaderUid) ? 'Líder 100' : 'Conductor',
            drivers: h.drivers || 0,
            customers: h.customers || 0,
            driversLabel: `${h.drivers || 0}/${dGoal}`,
            customersLabel: `${h.customers || 0}/${cGoal}`,
            driversOk: (h.drivers || 0) >= dGoal,
            customersOk: (h.customers || 0) >= cGoal,
            metaOk: ok,
          };
        });

        if (historyWeek === currentWeek) {
          list.forEach((u) => {
            if (u.usertype === 'driver' && u.leaderWeekly && !mapUid.has(u.id)) {
              const prog = formatWeeklyProgress ? formatWeeklyProgress(u.leaderWeekly) : null;
              const drivers = prog?.drivers || 0;
              const customers = prog?.customers || 0;
              if (drivers > 0 || customers > 0) {
                const driversOk = drivers >= dGoal;
                const customersOk = customers >= cGoal;
                rows.push({
                  id: `${u.id}_${historyWeek}`,
                  leaderUid: u.id,
                  weekKey: historyWeek,
                  leaderName: displayName(u),
                  cityName: u.cityName || '',
                  role: leadersSet.has(u.id) ? 'Líder 100' : 'Conductor',
                  drivers,
                  customers,
                  driversLabel: `${drivers}/${dGoal}`,
                  customersLabel: `${customers}/${cGoal}`,
                  driversOk,
                  customersOk,
                  metaOk: driversOk && customersOk,
                  updatedAt: u.leaderWeekly?.updatedAt || null,
                });
              }
            }
          });
        }
      }
    } else {
      // Todas las semanas (a partir del 19 de octubre de 2026)
      let filtered = (history || []).filter((h) => !h.weekKey || h.weekKey >= START_WEEK_KEY);
      if (historyLeaderScope === 'leaders') {
        filtered = filtered.filter((h) => leadersSet.has(h.leaderUid));
      }
      rows = filtered.map((h) => {
        const u = usersById[h.leaderUid];
        const ok = (h.drivers || 0) >= dGoal && (h.customers || 0) >= cGoal;
        return {
          ...h,
          leaderName: displayName(u) !== '—' ? displayName(u) : h.leaderUid,
          cityName: u?.cityName || '',
          role: leadersSet.has(h.leaderUid) ? 'Líder 100' : 'Conductor',
          drivers: h.drivers || 0,
          customers: h.customers || 0,
          driversLabel: `${h.drivers || 0}/${dGoal}`,
          customersLabel: `${h.customers || 0}/${cGoal}`,
          driversOk: (h.drivers || 0) >= dGoal,
          customersOk: (h.customers || 0) >= cGoal,
          metaOk: ok,
        };
      });
    }

    if (cityFilter) {
      rows = rows.filter((r) => usersById[r.leaderUid]?.cityId === cityFilter);
    }
    if (scopeIdSet) {
      rows = rows.filter((r) => scopeIdSet.has(r.leaderUid));
    }

    if (historyGoalFilter === 'met') {
      rows = rows.filter((r) => r.metaOk === true);
    } else if (historyGoalFilter === 'unmet') {
      rows = rows.filter((r) => r.metaOk === false);
    }

    rows.sort((a, b) => {
      if (a.weekKey !== b.weekKey) {
        return String(b.weekKey).localeCompare(String(a.weekKey));
      }
      if (a.metaOk !== b.metaOk) {
        return a.metaOk ? -1 : 1;
      }
      const totalA = (Number(a.drivers) || 0) + (Number(a.customers) || 0);
      const totalB = (Number(b.drivers) || 0) + (Number(b.customers) || 0);
      return totalB - totalA;
    });

    return rows;
  }, [
    tab,
    history,
    historyMap,
    historyWeek,
    historyLeaderScope,
    historyGoalFilter,
    currentWeek,
    staticusers,
    usersById,
    cityFilter,
    scopeIdSet,
    normalizeNetworkState,
    formatWeeklyProgress,
    WEEKLY_MIN_DRIVERS,
    WEEKLY_MIN_CUSTOMERS,
  ]);

  const historyStats = useMemo(() => {
    const total = historyRows.length;
    let metCount = 0;
    let totalDrivers = 0;
    let totalCustomers = 0;
    for (let i = 0; i < total; i += 1) {
      const r = historyRows[i];
      if (r.metaOk) metCount += 1;
      totalDrivers += Number(r.drivers) || 0;
      totalCustomers += Number(r.customers) || 0;
    }
    const unmetCount = total - metCount;
    const pct = total > 0 ? Math.round((metCount / total) * 100) : 0;
    return { total, metCount, unmetCount, pct, totalDrivers, totalCustomers };
  }, [historyRows]);

  const handleExportHistoryCsv = () => {
    if (!historyRows || historyRows.length === 0) {
      alert('No hay datos disponibles para exportar con los filtros seleccionados.');
      return;
    }
    const headers = [
      'Semana',
      'Conductor / Lider',
      'ID Conductor',
      'Telefono',
      'Correo',
      'Ciudad',
      'Rol',
      'Conductores Registrados',
      'Meta Conductores',
      'Cumplio Conductores',
      'Usuarios Registrados',
      'Meta Usuarios',
      'Cumplio Usuarios',
      'Meta General',
      'Ultima Actualizacion',
    ];

    const escapeCsv = (val) => {
      if (val == null) return '""';
      const s = String(val).replace(/"/g, '""');
      return `"${s}"`;
    };

    const csvRows = [
      headers.map(escapeCsv).join(','),
      ...historyRows.map((r) => {
        const u = usersById[r.leaderUid];
        return [
          escapeCsv(r.weekKey),
          escapeCsv(r.leaderName),
          escapeCsv(r.leaderUid),
          escapeCsv(u?.mobile || '—'),
          escapeCsv(u?.email || '—'),
          escapeCsv(r.cityName || u?.cityName || '—'),
          escapeCsv(r.role || 'Líder 100'),
          escapeCsv(r.drivers || 0),
          escapeCsv(WEEKLY_MIN_DRIVERS || 2),
          escapeCsv(r.driversOk ? 'SI' : 'NO'),
          escapeCsv(r.customers || 0),
          escapeCsv(WEEKLY_MIN_CUSTOMERS || 2),
          escapeCsv(r.customersOk ? 'SI' : 'NO'),
          escapeCsv(r.metaOk ? 'CUMPLIO' : 'NO CUMPLIO'),
          escapeCsv(r.updatedAt ? moment(r.updatedAt).format('YYYY-MM-DD HH:mm') : '—'),
        ].join(',');
      }),
    ];

    const blob = new Blob(['\uFEFF' + csvRows.join('\r\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const weekTag = historyWeek && historyWeek !== 'all' ? historyWeek : 'todas_las_semanas';
    const statusTag = historyGoalFilter !== 'all' ? `_${historyGoalFilter}` : '';
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `informe_metricas_lideres_${weekTag}${statusTag}_${moment().format('YYYYMMDD_HHmm')}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const cityOptions = useMemo(
    () =>
      [...cities].sort((a, b) =>
        String(a.name).localeCompare(String(b.name), 'es')
      ),
    [cities]
  );

  const cityName =
    cityOptions.find((c) => c.id === cityFilter)?.name || 'Todas';

  const tableStyle = useMemo(() => c2TableStyle(isRTL), [isRTL]);

  const tableOpts = useMemo(
    () => ({
      ...c2TableOptions(null),
      pageSize: 10,
      pageSizeOptions: [10, 25, 50],
      emptyRowsWhenPaging: false,
    }),
    []
  );

  const structureColumns = useMemo(
    () => [
      {
        title: 'Rol',
        field: 'role',
        render: (r) => (
          <C2StatusChip
            tone={String(r.role).startsWith('Principal') ? 'violet' : 'neutral'}
            label={r.role}
            dot={String(r.role).startsWith('Principal')}
          />
        ),
      },
      {
        title: 'Conductor',
        field: 'name',
        render: (r) => nameCell(r.name, r.cityName),
      },
      {
        title: 'Nivel',
        field: 'levelLabel',
        render: (r) => <C2StatusChip tone={r.level === 0 ? 'violet' : 'neutral'} label={r.levelLabel} dot={false} />,
      },
      {
        title: 'Estado estructura',
        field: 'networkStructureStatus',
        render: (r) =>
          api.networkStructureStatusLabel
            ? api.networkStructureStatusLabel(r.networkStructureStatus)
            : r.networkStructureStatus || '—',
      },
      {
        title: 'Código ingreso',
        field: 'signupEntryCode',
        render: (r) => codeCell(r.signupEntryCode),
      },
      { title: 'Ciudad', field: 'cityName', hidden: true },
      {
        title: 'Cupos',
        field: 'slots',
        render: (r) =>
          r.total > 0 ? (
            <C2ProgressBar used={r.used} total={r.total} done={r.used >= r.total} minWidth={100} />
          ) : (
            <C2StatusChip
              tone="neutral"
              label={formatDirectSlots ? formatDirectSlots(r.used, r.total) : `${r.used}/${r.total}`}
              dot={false}
            />
          ),
      },
      { title: 'Patrocinador', field: 'parentName' },
      {
        title: 'Alta',
        field: 'createdAt',
        render: (r) =>
          r.createdAt ? moment(r.createdAt).format('lll') : '—',
      },
    ],
    [formatDirectSlots]
  );

  const historyColumns = useMemo(
    () => [
      {
        title: 'Semana',
        field: 'weekKey',
        render: (r) => (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <span style={{ fontWeight: 600 }}>{r.weekKey}</span>
            {r.weekKey === currentWeek ? (
              <C2StatusChip tone="violet" label="Actual" />
            ) : null}
          </Box>
        ),
      },
      {
        title: 'Conductor / Líder',
        field: 'leaderName',
        render: (r) => nameCell(r.leaderName, r.cityName),
      },
      {
        title: 'Rol',
        field: 'role',
        render: (r) => (
          <C2StatusChip
            tone={r.role === 'Líder 100' ? 'violet' : 'default'}
            label={r.role || 'Conductor'}
          />
        ),
      },
      { title: 'Ciudad', field: 'cityName', hidden: true },
      {
        title: 'Cond. semana',
        field: 'driversLabel',
        render: (r) => <C2StatusChip tone={r.driversOk ? 'success' : 'danger'} label={r.driversLabel} />,
      },
      {
        title: 'Usu. semana',
        field: 'customersLabel',
        render: (r) => <C2StatusChip tone={r.customersOk ? 'success' : 'danger'} label={r.customersLabel} />,
      },
      {
        title: 'Meta',
        field: 'metaOk',
        render: (r) => (
          <C2StatusChip tone={r.metaOk ? 'success' : 'danger'} label={r.metaOk ? 'Cumplió' : 'No cumplió'} />
        ),
      },
      {
        title: 'Actualizado',
        field: 'updatedAt',
        render: (r) =>
          r.updatedAt ? moment(r.updatedAt).format('lll') : '—',
      },
    ],
    [currentWeek]
  );

  const seriesColumns = useMemo(
    () => [
      {
        title: 'Semana (lun)',
        field: 'weekKey',
        render: (r) => (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            <span>{moment(r.weekKey, 'YYYY-MM-DD').format('DD MMM YYYY')}</span>
            {r.weekKey === currentWeek ? <C2StatusChip tone="violet" label="Actual" /> : null}
          </Box>
        ),
      },
      { title: 'Nuevos conductores', field: 'drivers', type: 'numeric' },
      { title: 'Nuevos usuarios', field: 'customers', type: 'numeric' },
      { title: 'Altas N2', field: 'l2', type: 'numeric' },
      { title: 'Altas N3', field: 'l3', type: 'numeric' },
    ],
    [currentWeek]
  );

  const busy = loadingUsers && !staticusers;
  const dash = busy ? '…' : null;

  const goal = metrics.weeklyGoal;
  const goalAccent = goal.pct >= 70 ? '#16A34A' : goal.leaders > 0 ? '#DC2626' : C2.violet;
  const driverName = driverFilter ? driverFilter.label.split(' · ')[0] : null;
  const emptyLoc = (icon, title, message) =>
    c2Localization(localization(t), <C2EmptyState icon={icon} title={title} message={message} />);
  const scopeSuffix = cityFilter || driverFilter ? ' en este alcance' : '';

  return (
    <Box sx={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
      <ThemeProvider theme={c2TableTheme}>
        <C2TableCard sx={{ mb: 2, p: { xs: 1.5, sm: 2 } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box sx={{ '& .MuiFormControl-root': { width: '100%' } }}>
                <C2Select
                  id="metricas-city-filter"
                  label="Ciudad"
                  value={cityFilter}
                  onChange={(e) => {
                    setCityFilter(e.target.value);
                    setDriverFilter(null);
                  }}
                  options={[
                    { value: '', label: 'Todas' },
                    ...cityOptions.map((c) => ({ value: c.id, label: c.name })),
                  ]}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Autocomplete
                size="small"
                options={metrics.driverOptions}
                value={driverFilter}
                onChange={(_, v) => setDriverFilter(v)}
                getOptionLabel={(o) => o?.label || ''}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                filterSelectedOptions
                noOptionsText="Sin conductores en la red"
                slotProps={{ paper: { sx: { fontFamily: C2_FONT, borderRadius: '12px', border: `1px solid ${C2.border}` } } }}
                renderInput={(params) => (
                  <TextField {...params} label="Conductor" placeholder="Todos" sx={c2TextFieldSx} />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                <ScopePill icon={PlaceOutlinedIcon}>
                  {cityName}
                  {driverName ? ` · ${driverName}` : ''}
                </ScopePill>
                {currentWeek ? (
                  <ScopePill icon={EventOutlinedIcon}>
                    Semana del {moment(currentWeek, 'YYYY-MM-DD').format('DD MMM')}
                  </ScopePill>
                ) : null}
              </Box>
            </Grid>
          </Grid>
          {busy ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
              <CircularProgress size={14} sx={{ color: C2.violet }} />
              <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: C2.muted }}>Cargando usuarios…</Typography>
            </Box>
          ) : null}
        </C2TableCard>

        <GroupLabel>Crecimiento</GroupLabel>
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid item xs={12} sm={6} lg={3}>
            <C2SummaryCard
              icon={DirectionsCarOutlinedIcon}
              label="Conductores registrados"
              value={dash ?? metrics.scopedDrivers}
              caption={`En alcance · ${metrics.allDrivers} en total`}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <C2SummaryCard
              icon={PeopleAltOutlinedIcon}
              label="Usuarios registrados"
              value={dash ?? metrics.scopedCustomers}
              caption={`En alcance · ${metrics.allCustomers} en total`}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <C2SummaryCard
              icon={CarRentalOutlinedIcon}
              label="Nuevos conductores esta semana"
              value={dash ?? `+${metrics.newDrivers}`}
              caption={currentWeek ? `Desde el lunes ${moment(currentWeek, 'YYYY-MM-DD').format('DD MMM')}` : null}
              accent="#16A34A"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <C2SummaryCard
              icon={PersonAddAltOutlinedIcon}
              label="Nuevos usuarios esta semana"
              value={dash ?? `+${metrics.newCustomers}`}
              caption={currentWeek ? `Desde el lunes ${moment(currentWeek, 'YYYY-MM-DD').format('DD MMM')}` : null}
              accent="#16A34A"
            />
          </Grid>
        </Grid>

        <GroupLabel>Red de líderes</GroupLabel>
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid item xs={12} sm={6} lg={3}>
            <C2SummaryCard
              icon={FlagOutlinedIcon}
              label="Cumplimiento meta semanal"
              value={dash ?? `${goal.pct}%`}
              caption={`${goal.onTrack} en meta · ${goal.offTrack} bajo · ${goal.completedN1} completó N1`}
              accent={goalAccent}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <C2SummaryCard
              icon={StarsOutlinedIcon}
              label="Cupos Nivel 1"
              value={dash ?? `${metrics.l1Slots.available} libres`}
              caption={`${metrics.l1Slots.principals} principales`}
            >
              {!busy && metrics.l1Slots.total > 0 ? (
                <Box sx={{ mt: 0.75 }}>
                  <C2ProgressBar
                    used={metrics.l1Slots.used}
                    total={metrics.l1Slots.total}
                    done={metrics.l1Slots.available === 0}
                    minWidth={0}
                  />
                </Box>
              ) : null}
            </C2SummaryCard>
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <C2SummaryCard
              icon={AccountTreeOutlinedIcon}
              label="Integrantes Nivel 2"
              value={dash ?? metrics.levelCounts[2]}
              caption={`+${metrics.l2Week} esta semana`}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <C2SummaryCard
              icon={LanOutlinedIcon}
              label="Integrantes Nivel 3"
              value={dash ?? metrics.levelCounts[3]}
              caption={`+${metrics.l3Week} esta semana`}
            />
          </Grid>
        </Grid>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={c2PillTabsSx}
        >
          <Tab icon={<AccountTreeOutlinedIcon fontSize="small" />} iconPosition="start" label="Estructura descendente" />
          <Tab icon={<HistoryOutlinedIcon fontSize="small" />} iconPosition="start" label="Historial por conductor" />
          <Tab icon={<ShowChartOutlinedIcon fontSize="small" />} iconPosition="start" label="Crecimiento semanal" />
        </Tabs>

        <TabPanel value={tab} index={0}>
          <C2TableCard>
            <C2SectionHeading
              title={driverFilter ? `Red de ${driverName}` : 'Principales del alcance'}
              subtitle={
                driverFilter
                  ? 'Conductor seleccionado y su red descendente (N1–N3).'
                  : 'Principales (Nivel 0). Elige un conductor arriba para ver su estructura completa.'
              }
            />
            {!busy ? (
              <MaterialTable
                title={t('list') || 'Listado'}
                columns={structureColumns}
                data={structureRows}
                style={tableStyle}
                options={tableOpts}
                localization={emptyLoc(
                  AccountTreeOutlinedIcon,
                  `Sin principales${scopeSuffix}`,
                  'Cuando se registren líderes con cupo especial de ciudad, aparecerán aquí.'
                )}
              />
            ) : (
              <LoadingBlock label="Cargando estructura…" />
            )}
          </C2TableCard>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <C2TableCard>
            <C2SectionHeading
              title="Historial por conductor"
              subtitle={`Meta semanal: ${WEEKLY_MIN_DRIVERS || 2} conductores + ${WEEKLY_MIN_CUSTOMERS || 2} usuarios por líder. ${historyRows.length} registros encontrados.`}
              action={
                <Button
                  id="btn-download-history-csv"
                  variant="contained"
                  startIcon={<FileDownloadOutlinedIcon />}
                  onClick={handleExportHistoryCsv}
                  disabled={!historyRows.length}
                  sx={{
                    backgroundColor: C2.violet,
                    color: '#fff',
                    fontFamily: C2_FONT,
                    fontWeight: 700,
                    fontSize: '0.82rem',
                    textTransform: 'none',
                    borderRadius: '10px',
                    px: 2,
                    py: 0.85,
                    boxShadow: '0 4px 14px rgba(100, 17, 237, 0.25)',
                    '&:hover': {
                      backgroundColor: C2.deep,
                    },
                    '&.Mui-disabled': {
                      backgroundColor: 'rgba(0,0,0,0.12)',
                      color: 'rgba(0,0,0,0.26)',
                    },
                  }}
                >
                  Descargar informe CSV ({historyRows.length})
                </Button>
              }
            />

            {/* Barra de Filtros del Historial */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2.5,
                p: 1.5,
                backgroundColor: C2.lavender,
                borderRadius: '12px',
                border: `1px solid ${C2.border}`,
              }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', flex: 1 }}>
                <C2Select
                  id="history-week-filter"
                  label="Semana"
                  value={historyWeek}
                  onChange={(e) => setHistoryWeek(e.target.value)}
                  minWidth={230}
                  options={[
                    { value: 'all', label: 'Todas las semanas' },
                    ...availableWeeks,
                  ]}
                />
                <C2Select
                  id="history-scope-filter"
                  label="Grupo / Líderes"
                  value={historyLeaderScope}
                  onChange={(e) => setHistoryLeaderScope(e.target.value)}
                  minWidth={230}
                  options={[
                    { value: 'leaders', label: '⭐ 100 Líderes principales' },
                    { value: 'all', label: 'Todos los conductores con historial' },
                  ]}
                />
                <C2Select
                  id="history-goal-filter"
                  label="Cumplimiento de objetivos"
                  value={historyGoalFilter}
                  onChange={(e) => setHistoryGoalFilter(e.target.value)}
                  minWidth={250}
                  options={[
                    { value: 'all', label: 'Todos (cumplieron y no cumplieron)' },
                    { value: 'met', label: `✓ Cumplieron meta (≥${WEEKLY_MIN_DRIVERS || 2} cond + ${WEEKLY_MIN_CUSTOMERS || 2} usu)` },
                    { value: 'unmet', label: '✗ No cumplieron meta' },
                  ]}
                />
              </Box>
            </Box>

            {/* KPIs Ejecutivos de la Semana */}
            <Grid container spacing={2} sx={{ mb: 2.5 }}>
              <Grid item xs={12} sm={6} md={3}>
                <C2SummaryCard
                  icon={AccountTreeOutlinedIcon}
                  label={historyLeaderScope === 'leaders' ? 'Líderes evaluados' : 'Conductores evaluados'}
                  value={historyStats.total}
                  caption={
                    historyWeek && historyWeek !== 'all'
                      ? `Semana del ${moment(historyWeek, 'YYYY-MM-DD').format('DD MMM')}`
                      : 'Acumulado histórico'
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <C2SummaryCard
                  icon={CheckCircleOutlineOutlinedIcon}
                  label="Cumplieron objetivo"
                  value={`${historyStats.metCount} (${historyStats.pct}%)`}
                  caption={`Mínimo ${WEEKLY_MIN_DRIVERS || 2} cond. + ${WEEKLY_MIN_CUSTOMERS || 2} usu.`}
                  accent="#16A34A"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <C2SummaryCard
                  icon={HighlightOffOutlinedIcon}
                  label="Bajo objetivo"
                  value={historyStats.unmetCount}
                  caption="No alcanzaron las metas"
                  accent="#DC2626"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <C2SummaryCard
                  icon={PersonAddAltOutlinedIcon}
                  label="Altas en la semana"
                  value={`+${historyStats.totalDrivers + historyStats.totalCustomers}`}
                  caption={`${historyStats.totalDrivers} cond. · ${historyStats.totalCustomers} usuarios`}
                  accent={C2.violet}
                />
              </Grid>
            </Grid>

            {historyLoading ? (
              <LoadingBlock label="Cargando historial…" />
            ) : (
              <MaterialTable
                title="Historial"
                columns={historyColumns}
                data={historyRows}
                style={tableStyle}
                options={tableOpts}
                localization={emptyLoc(
                  HistoryOutlinedIcon,
                  `Sin historial${scopeSuffix}`,
                  'No se encontraron registros para la semana o filtros seleccionados.'
                )}
              />
            )}
          </C2TableCard>
        </TabPanel>

        <TabPanel value={tab} index={2}>
          <C2TableCard>
            <C2SectionHeading
              title="Crecimiento semanal"
              subtitle="Altas por semana (últimas ~8) según fecha de registro, filtradas por ciudad y conductor."
            />
            {!busy ? (
              <>
                {metrics.growthSeries.length ? <GrowthChart series={metrics.growthSeries} /> : null}
                <MaterialTable
                  title="Serie semanal"
                  columns={seriesColumns}
                  data={metrics.growthSeries}
                  style={tableStyle}
                  options={{ ...tableOpts, paging: false }}
                  localization={emptyLoc(
                    ShowChartOutlinedIcon,
                    'Sin datos de crecimiento',
                    'Aún no hay registros en las últimas semanas para este alcance.'
                  )}
                />
              </>
            ) : (
              <LoadingBlock label="Cargando serie…" />
            )}
          </C2TableCard>
        </TabPanel>
      </ThemeProvider>
    </Box>
  );
}
